import { createSelector, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "store";
import { type Dish, type Order, type Refill, TransactionMode, TransactionState } from "types";

// The single transaction the UI is currently following — at most one of order/refill at a time.
export type TransactionSlot = { kind: "order"; transaction: Order } | { kind: "refill"; transaction: Refill } | null;

// One subscription push: the BE reports a flow in EITHER `orders.last` OR `refills.last`
// (the same flow can migrate between fields), and both may echo stale finished transactions.
export type TransactionFrame = { order?: Order | null; refill?: Refill | null };

// "Active" for slot ownership means any non-Finished state — StockCheck still owns the slot.
const isActive = (transaction: Order | Refill) => transaction.state !== TransactionState.Finished;
const orderSlot = (transaction: Order): TransactionSlot => ({ kind: "order", transaction: transaction });
const refillSlot = (transaction: Refill): TransactionSlot => ({ kind: "refill", transaction: transaction });

// True only while a non-finished ORDER owns the slot. The dish reconcile uses this to pick frame
// semantics: during an order the BE streams SPARSE `dishes.types` deltas (absence = unchanged → merge
// only), while refills and order-finish send FULL snapshots (absence = the last dish was taken → drop the
// type, which is wanted: a refilled type must disappear once its last dish is removed). See
// setDishTypes(reconcileRemovals) in dishesSlice and the routing in DeviceContext.
export const isActiveOrderSlot = (slot: TransactionSlot): boolean =>
	slot?.kind === "order" && isActive(slot.transaction);

// Pure resolver: given the slot we currently hold and a fresh frame, decide which transaction
// owns the slot. Rules R1–R7 below are ordered by priority.
export const pickTransaction = (current: TransactionSlot, frame: TransactionFrame): TransactionSlot => {
	// R1: an active refill (local or remote) always wins — the backend forbids starting a new
	// transaction while one is active, so an active refill in the frame means anything else is stale.
	if (frame.refill && isActive(frame.refill)) {
		return refillSlot(frame.refill);
	}
	// R2: otherwise an active order wins for the same reason.
	if (frame.order && isActive(frame.order)) {
		return orderSlot(frame.order);
	}
	// Only finished (or absent) candidates remain past this point.
	// R3: a frame payload with the slot's own id is an update to the transaction we already follow
	// (e.g. Active → Finished) — always land it so the flow can reach its summary step.
	if (current?.kind === "order" && frame.order && frame.order.id === current.transaction.id) {
		return orderSlot(frame.order);
	}
	if (current?.kind === "refill" && frame.refill && frame.refill.id === current.transaction.id) {
		return refillSlot(frame.refill);
	}
	// R4: the BE reports a flow in either field — when we hold a RUNNING order but the frame carries
	// only a LOCAL refill, the flow moved to `refills`, so the held order is stale and would mask the
	// refill (order takes precedence in selectActiveLocalTransaction), hanging the UI on OrderStep3.
	// Remote refills are excluded: they run in their own modal and never continue an order flow.
	// A FINISHED order is excluded too: it owns the summary screen until reset, and a stale
	// refill echo must not steal it (R7's philosophy).
	if (
		current?.kind === "order" &&
		isActive(current.transaction) &&
		frame.refill &&
		frame.refill.mode !== TransactionMode.Remote &&
		!frame.order
	) {
		return refillSlot(frame.refill);
	}
	// R5: we hold an Active transaction but the frame's same-kind candidate has a different id —
	// the held Active payload is stale and the candidate takes the slot. Covers the reconnect gap
	// (we missed the held transaction's finish while offline) as well as any same-kind churn.
	if (current && isActive(current.transaction)) {
		if (current.kind === "order" && frame.order && frame.order.id !== current.transaction.id) {
			return orderSlot(frame.order);
		}
		if (current.kind === "refill" && frame.refill && frame.refill.id !== current.transaction.id) {
			return refillSlot(frame.refill);
		}
	}
	// R6: empty slot — take whatever the frame offers, order first (matches the order precedence
	// in selectActiveLocalTransaction), so the UI can show the last finished transaction's summary.
	if (current === null) {
		if (frame.order) {
			return orderSlot(frame.order);
		}
		if (frame.refill) {
			return refillSlot(frame.refill);
		}
		return null;
	}
	// R7: an occupied slot keeps against stale strangers — a finished different-kind echo or an
	// empty frame must not knock out the transaction whose summary the user may still be viewing.
	return current;
};

type TransactionSlice = {
	slot: TransactionSlot;
	currentOrderStep: number;
};

const initialState: TransactionSlice = {
	slot: null,
	currentOrderStep: 1,
};

export const transactionSlice = createSlice({
	name: "transaction",
	initialState: initialState,
	reducers: {
		setTransaction: (state, { payload }: PayloadAction<TransactionFrame>) => {
			state.slot = pickTransaction(state.slot, payload);
		},
		resetTransaction: (state) => {
			state.slot = null;
			state.currentOrderStep = 1;
		},
		clearTransactionSlot: (state) => {
			state.slot = null;
		},
		updateCurrentOrderStep: (state, action: PayloadAction<number>) => {
			state.currentOrderStep = action.payload;
		},
	},
});

export const { clearTransactionSlot, resetTransaction, setTransaction, updateCurrentOrderStep } =
	transactionSlice.actions;

const selectSlot = (state: RootState): TransactionSlot => state.transaction?.slot ?? null;

export const selectOrder = createSelector(selectSlot, (slot): Order | null =>
	slot?.kind === "order" ? slot.transaction : null,
);
// Primitive projection of the order's state. Every subscription frame replaces the order object (new
// reference), so subscribing to the whole order re-renders a component every frame; subscribing to this
// enum re-renders it only when the state actually transitions. For per-item components (e.g. DishTypeItem)
// that care only about the state, not the order payload.
export const selectOrderState = createSelector(selectOrder, (order): TransactionState | null => order?.state ?? null);
export const selectRefill = createSelector(selectSlot, (slot): Refill | null =>
	slot?.kind === "refill" ? slot.transaction : null,
);
export const selectHasActiveOrder = createSelector(selectOrder, (order) => order !== null);
export const selectUserPreferences = createSelector(selectOrder, (order) => order?.user?.preferences || null);
export const selectOrderPaymentMethod = createSelector(selectOrder, (order) => order?.payment?.method?.type || null);
export const selectRefillMode = createSelector(selectRefill, (refill): TransactionMode | null => {
	if (!refill || refill.state === TransactionState.Finished) {
		return null;
	}
	return refill.mode || null;
});
// Remote refills are owned by RemoteRefillModal, so they never surface here. Finished local
// transactions stay in the slot for the OrderStep5 summary until resetTransaction clears it.
export const selectActiveLocalTransaction = createSelector(selectSlot, (slot): Order | Refill | null => {
	if (slot === null) {
		return null;
	}
	if (slot.kind === "refill" && slot.transaction.mode === TransactionMode.Remote) {
		return null;
	}
	return slot.transaction;
});
// True while an order or local refill is RUNNING (not Finished). A boolean so subscribers
// re-render on the running/idle edge only, not on every subscription frame that lands a
// fresh transaction payload in the slot.
export const selectIsLocalTransactionActive = createSelector(
	selectActiveLocalTransaction,
	(transaction) => transaction !== null && isActive(transaction),
);

// Unlike selectActiveLocalTransaction, these cover EVERY transaction kind in the slot — including
// remote refills (which RemoteRefillModal owns and the "local" selectors deliberately hide). Used by
// the QR-lifecycle logging, which must observe start/end for orders, local refills and remote refills.
export type TransactionDescriptor = { id: string; type: "order" | "local-refill" | "remote-refill" };
export const selectAnyTransaction = createSelector(
	selectSlot,
	(slot): Order | Refill | null => slot?.transaction ?? null,
);
export const selectIsAnyTransactionActive = createSelector(
	selectAnyTransaction,
	(transaction) => transaction !== null && isActive(transaction),
);
export const selectTransactionDescriptor = createSelector(selectSlot, (slot): TransactionDescriptor | null => {
	if (slot === null) {
		return null;
	}
	if (slot.kind === "order") {
		return { id: slot.transaction.id, type: "order" };
	}
	return {
		id: slot.transaction.id,
		type: slot.transaction.mode === TransactionMode.Remote ? "remote-refill" : "local-refill",
	};
});
export const selectCurrentOrderStep = (state: RootState) => state.transaction.currentOrderStep;
// Sourced from the active local transaction so refill step 3/5 drawers see removed dishes too.
export const selectTransactionDishes = createSelector(
	selectActiveLocalTransaction,
	(transaction) => transaction?.inventory?.removed || [],
);
export const selectIsEmptyOrder = createSelector(selectActiveLocalTransaction, (transaction) => {
	if (!transaction) {
		return false;
	}
	const hasEmptyInventory = transaction.inventory?.removed?.length === 0 || !transaction.inventory;
	return hasEmptyInventory && transaction.state === TransactionState.Finished;
});

// A dish carrying `price.default` is discounted — `default` holds the pre-discount price.
const isDiscounted = (dish: Dish) => dish.price?.default != null;

const countByDishType = (dishes: Dish[]): Record<string, number> =>
	dishes.reduce(
		(acc, dish) => {
			const dishTypeId = dish.type?.id;
			if (dishTypeId) {
				acc[dishTypeId] = (acc[dishTypeId] || 0) + 1;
			}
			return acc;
		},
		{} as Record<string, number>,
	);

// TODO: add fetching logic for when there are unknown dish types in the order
export const selectOrderDishTypes = createSelector(
	[(state: RootState) => state.dishes.types, selectActiveLocalTransaction],
	(fridgeDishes, transaction) => {
		if (!(fridgeDishes && transaction?.inventory?.removed)) {
			return [];
		}
		const orderDishes = transaction.inventory.removed;
		const fullPriceDishes = orderDishes.filter((dish) => !isDiscounted(dish));
		const orderDishTypeIds = new Set(fullPriceDishes.map((dish) => dish?.type?.id));
		// Keyed over ALL removed dishes (not just full-price ones) — a later discounted dish of the
		// same type intentionally overrides the price shown for that type.
		const orderDishesById = new Map(orderDishes.map((dish) => [dish.type?.id, dish]));
		return fridgeDishes
			.filter((dishType) => orderDishTypeIds.has(dishType.id))
			.map((dishType) => {
				const orderDish = orderDishesById.get(dishType.id);
				return {
					...dishType,
					...(orderDish?.price && {
						price: { ...dishType.price, ...orderDish.price },
					}),
				};
			});
	},
);

export const selectDiscountedOrderDishTypes = createSelector(
	[(state: RootState) => state.dishes.types, selectActiveLocalTransaction],
	(fridgeDishes, transaction) => {
		if (!(fridgeDishes && transaction?.inventory?.removed)) {
			return [];
		}
		const discountedDishes = transaction.inventory.removed.filter(isDiscounted);
		const discountedDishTypeIds = new Set(discountedDishes.map((dish) => dish?.type?.id));
		const discountedDishesById = new Map(discountedDishes.map((dish) => [dish.type?.id, dish]));

		return fridgeDishes
			.filter((dishType) => discountedDishTypeIds.has(dishType.id))
			.map((dishType) => {
				const discountedDishPrice = discountedDishesById.get(dishType.id)?.price;
				return {
					...dishType,
					// The dish's own price replaces (not merges) the catalog price — it already
					// carries `default` (the original price) alongside the discounted value.
					...(discountedDishPrice && {
						price: { ...discountedDishPrice },
					}),
				};
			});
	},
);

export const selectOrderSummary = createSelector(
	selectActiveLocalTransaction,
	selectOrder,
	selectOrderDishTypes,
	selectDiscountedOrderDishTypes,
	(transaction, order, orderDishTypes, discountedDishTypes) => {
		if (!transaction) {
			return null;
		}

		const orderDishes = transaction.inventory?.removed || [];
		const orderDishTypeIds = new Set(orderDishes.map((dish) => dish.type?.id).filter((id) => id != null));

		return {
			orderDishTypesCount: orderDishTypeIds.size,
			orderDishTypes: orderDishTypes,
			discountedDishTypes: discountedDishTypes,
			// Refills don't carry a price; OrderPriceSummary tolerates undefined.
			orderPrice: order?.price,
			fullPricedDishCounts: countByDishType(orderDishes.filter((dish) => !isDiscounted(dish))),
			discountedDishCounts: countByDishType(orderDishes.filter(isDiscounted)),
		};
	},
);

export default transactionSlice.reducer;
